// Getting frames out of things that move.
//
// Two sources, one shape of answer: `{ bitmaps, delaysMs }`, ready for the
// dither pipeline, which does not care where a frame came from.
//
// THE CAP IS DELIBERATE. A fax is a picture of a page, not a video player, and
// every frame is a full 1080x1428 dither — so the cost is real and the joke does
// not get funnier after a dozen frames. Twelve at ~110ms reads as motion and
// keeps a page under a megabyte for ordinary content.

/* global ImageDecoder */
import { parseGif, compositeGif } from './gifdecode.js';

export const MAX_FRAMES = 24;      // a searched GIF: enough for a loop
export const CLIP_SECONDS = 5;
// A video clip: five seconds at eight frames a second. Twelve over five
// seconds was 2.4fps, and it showed. Memory is not the constraint it was — the
// GIF is written a frame at a time — so the cost is only render time inside
// the transmission, which the honest third of the bar already covers.
export const CLIP_FRAMES = 40;

/**
 * `seeked` is a promise about TIME, not about PIXELS. iOS fires it before the
 * frame at that time has been decoded and composited, and a drawImage() in
 * that gap paints black — which came out of the machine as a solid column with
 * a few dithered bands where a frame had partly arrived. `requestVideoFrameCallback`
 * resolves when a frame is genuinely presented; where it is missing, two
 * animation frames after `timeupdate` is the closest the platform offers.
 */
function paintedFrame(v, maxMs = 250) {
  return new Promise((res) => {
    // BOUNDED. rVFC fires only when a NEW frame is presented; a paused video
    // whose seek lands on the frame already on screen presents nothing, and
    // the first version of this waited forever — the trimmer sat on "READING
    // THE TAPE…" with no way out. The callback wins when it comes; the clock
    // wins when it does not, and two animation frames is still enough for the
    // compositor to have caught up on every platform tried.
    let done = false;
    const finish = () => { if (!done) { done = true; res(); } };
    if (typeof v.requestVideoFrameCallback === 'function') v.requestVideoFrameCallback(finish);
    requestAnimationFrame(() => requestAnimationFrame(() => setTimeout(finish, 16)));
    setTimeout(finish, maxMs);
  });
}

/**
 * A duration iOS will actually admit to.
 *
 * With a blob: URL and `preload="metadata"`, WKWebView reports `Infinity` (or
 * never fires `loadedmetadata`) for a .mov until it is pushed. Seeking past the
 * end forces it to work the real length out and report it on `durationchange`.
 */
function settleDuration(v, ms = 8000) {
  return new Promise((res) => {
    let done = false;
    const finish = () => { if (done) return; done = true; res(Number.isFinite(v.duration) ? v.duration : 0); };
    const nudge = () => {
      if (Number.isFinite(v.duration) && v.duration > 0) return finish();
      try { v.currentTime = 1e6; } catch { /* not ready yet */ }
    };
    v.addEventListener('loadedmetadata', nudge, { once: true });
    v.addEventListener('durationchange', () => { if (Number.isFinite(v.duration) && v.duration > 0) finish(); });
    v.addEventListener('error', finish, { once: true });
    setTimeout(finish, ms);
    v.load();
    nudge();
  });
}

/** Even coverage across N frames, so a long source does not sample only its start. */
const pickIndices = (total, want) => {
  if (total <= want) return [...Array(total).keys()];
  const step = total / want;
  return [...Array(want).keys()].map((i) => Math.min(total - 1, Math.round(i * step)));
};

/**
 * Frames out of an animated GIF's bytes.
 *
 * `ImageDecoder` is WebCodecs, not canvas: it hands back every frame with its
 * own duration, which a `<img>` tag cannot do at all — an animated GIF drawn to
 * a canvas gives you whichever frame happened to be showing.
 */
export async function framesFromGif(bytes, { max = MAX_FRAMES } = {}) {
  // WebKit has no ImageDecoder. That is every iPhone, so the hand decoder is
  // not a corner case: it is what the app ships on. Chrome keeps the native
  // one because it is faster and knows every GIF dialect there is; if it
  // chokes on one, the hand decoder gets a turn before anyone sees a blank.
  if (typeof ImageDecoder === 'undefined') return framesFromGifByHand(bytes, { max });
  try {
    return await framesFromGifNative(bytes, { max });
  } catch (e) {
    if (e?.code === 'empty') throw e;
    return framesFromGifByHand(bytes, { max });
  }
}

async function framesFromGifByHand(bytes, { max }) {
  const parsed = parseGif(bytes);
  const total = parsed.frames.length;
  const want = pickIndices(total, max);
  const { bitmaps, delaysMs } = await compositeGif(parsed, want);
  return stretched(bitmaps, delaysMs.map((ms) => (ms >= 20 ? ms : 100)), total);
}

/** Sampled frames must still add up to the original running time. */
function stretched(bitmaps, delaysMs, total) {
  if (total > bitmaps.length) {
    const factor = total / bitmaps.length;
    for (let i = 0; i < delaysMs.length; i++) delaysMs[i] = Math.round(delaysMs[i] * factor);
  }
  return { bitmaps, delaysMs };
}

async function framesFromGifNative(bytes, { max }) {
  const dec = new ImageDecoder({ data: bytes, type: 'image/gif' });
  await dec.tracks.ready;
  await dec.completed;
  const track = dec.tracks.selectedTrack;
  const total = track?.frameCount ?? 0;
  if (!total) throw Object.assign(new Error('That GIF has no frames.'), { code: 'empty' });

  const want = pickIndices(total, max);
  const bitmaps = [];
  const delaysMs = [];
  for (const i of want) {
    const { image } = await dec.decode({ frameIndex: i });
    bitmaps.push(await createImageBitmap(image));
    // A GIF that says 0 means "as fast as you can", which every browser quietly
    // reads as 100ms. Match that rather than producing a strobe.
    const ms = Math.round((image.duration || 0) / 1000);
    delaysMs.push(ms >= 20 ? ms : 100);
    image.close();
  }
  dec.close();
  return stretched(bitmaps, delaysMs, total);
}

/**
 * Frames out of a video file, from a chosen window.
 *
 * Seek-and-draw rather than `VideoDecoder`: it needs no codec configuration, it
 * works with whatever the platform can already play, and at twelve frames the
 * speed difference does not matter. What it CANNOT do is decode a format the
 * platform refuses — an iPhone's HEVC .mov plays on the phone and not in a
 * desktop browser — so the caller has to be ready for `cannot-play`.
 */
export async function framesFromVideo(file, {
  startSec = 0, seconds = CLIP_SECONDS, max = CLIP_FRAMES,
} = {}) {
  const url = URL.createObjectURL(file);
  const v = document.createElement('video');
  v.preload = 'auto';
  v.muted = true;
  v.playsInline = true;
  v.src = url;

  const fail = (code, message) => Object.assign(new Error(message), { code });

  try {
    const duration = await settleDuration(v);
    if (!duration) throw fail('cannot-play', 'This machine cannot read that video.');
    const start = Math.max(0, Math.min(startSec, Math.max(0, duration - 0.05)));
    const span = Math.min(seconds, Math.max(0.1, duration - start));

    const canvas = document.createElement('canvas');
    canvas.width = v.videoWidth || 640;
    canvas.height = v.videoHeight || 480;
    const ctx = canvas.getContext('2d', { willReadFrequently: false });

    const bitmaps = [];
    for (let i = 0; i < max; i++) {
      const t = start + (span * i) / max;
      await new Promise((res, rej) => {
        // A seek to where the head already is fires no `seeked` at all.
        if (Math.abs(v.currentTime - t) < 0.02) { res(); return; }
        const timer = setTimeout(() => rej(fail('cannot-play', 'That video stopped part way through.')), 6000);
        v.onseeked = () => { clearTimeout(timer); res(); };
        v.onerror = () => { clearTimeout(timer); rej(fail('cannot-play', 'That video stopped part way through.')); };
        v.currentTime = t;
      });
      await paintedFrame(v);
      ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
      bitmaps.push(await createImageBitmap(canvas));
    }

    const per = Math.max(40, Math.round((span * 1000) / max));
    return { bitmaps, delaysMs: bitmaps.map(() => per) };
  } finally {
    v.removeAttribute('src');
    v.load();
    URL.revokeObjectURL(url);
  }
}

/** How long a video is, for the scrubber, without decoding a single frame. */
export function videoDuration(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement('video');
    v.preload = 'auto';
    v.muted = true;
    v.playsInline = true;
    v.src = url;
    settleDuration(v).then((d) => { URL.revokeObjectURL(url); resolve(d); });
  });
}
